import React, { useState } from 'react';
import { Clock, BookOpen, ChevronRight, Filter } from 'lucide-react';
import { cricketArticles, articleCategories } from '../data/cricketArticles';

const ArticlesSection = () => {
  const [selectedCategory, setSelectedCategory] = useState('All Articles');
  const [expandedArticle, setExpandedArticle] = useState(null);

  const filteredArticles = selectedCategory === 'All Articles'
    ? cricketArticles
    : cricketArticles.filter(article => article.category === selectedCategory);

  return (
    <section id="articles" className="bg-gray-50 py-24">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-5xl font-bold text-black tracking-[0.05em] mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Cricket Knowledge Hub
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Technical articles and in-depth guides to master every aspect of cricket
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {articleCategories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-5 py-2 text-sm rounded-full transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-black text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-200 border border-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Articles Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredArticles.map((article) => (
            <div
              key={article.id}
              className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 cursor-pointer group"
              onClick={() => setExpandedArticle(article)}
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3">
                  <span className={`px-3 py-1 text-xs rounded-full ${
                    article.difficulty === 'Expert' ? 'bg-red-500 text-white' :
                    article.difficulty === 'Advanced' ? 'bg-orange-500 text-white' :
                    'bg-green-500 text-white'
                  }`}>
                    {article.difficulty}
                  </span>
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                  <span className="flex items-center gap-1">
                    <BookOpen size={14} />
                    {article.category}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    {article.readTime}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-gray-700 transition-colors">
                  {article.title}
                </h3>
                <p className="text-gray-600 text-sm line-clamp-2">
                  {article.excerpt}
                </p>
                <div className="mt-4 flex items-center text-black text-sm font-medium">
                  Read More <ChevronRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Article Modal */}
      {expandedArticle && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setExpandedArticle(null)}
        >
          <div
            className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-64">
              <img
                src={expandedArticle.image}
                alt={expandedArticle.title}
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => setExpandedArticle(null)}
                className="absolute top-4 right-4 bg-black/50 text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-black transition-colors"
              >
                ×
              </button>
            </div>
            <div className="p-8">
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                <span className={`px-3 py-1 rounded-full ${
                  expandedArticle.difficulty === 'Expert' ? 'bg-red-100 text-red-600' :
                  expandedArticle.difficulty === 'Advanced' ? 'bg-orange-100 text-orange-600' :
                  'bg-green-100 text-green-600'
                }`}>
                  {expandedArticle.difficulty}
                </span>
                <span>{expandedArticle.category}</span>
                <span>{expandedArticle.readTime} read</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Georgia, serif' }}>
                {expandedArticle.title}
              </h2>
              <div className="prose prose-gray max-w-none">
                {expandedArticle.content.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="mb-4 text-gray-700 leading-relaxed whitespace-pre-line">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ArticlesSection;
